import { normalizarLista } from './utils';

const META_COBRANCA = 0.8;

export const FILTROS_VISUAIS_INICIAIS = {
  empreendimento: null,
  tipo_parcela: null,
  situacao: null,
};

const getNumero = valor => {
  if (valor == null || Number.isNaN(Number(valor))) return 0;
  return Number(valor);
};

const getNumeroCampo = (item, campos) => {
  if (!item) return 0;

  const campo = campos.find(chave => item[chave] != null);
  return getNumero(campo ? item[campo] : null);
};

const normalizarTexto = valor => (valor == null ? '' : `${valor}`);

export const chaveEmpreendimento = item => {
  if (item && item.codigo_empreendimento != null) {
    return `codigo-${item.codigo_empreendimento}`;
  }

  return `nome-${normalizarTexto(item && item.empreendimento)}`;
};

const mesmoEmpreendimento = (filtro, item) =>
  filtro && chaveEmpreendimento(filtro) === chaveEmpreendimento(item);

const passaFiltros = (item, filtros, ignorar) => {
  if (
    ignorar !== 'empreendimento' &&
    filtros.empreendimento &&
    !mesmoEmpreendimento(filtros.empreendimento, item)
  ) {
    return false;
  }

  if (
    ignorar !== 'tipo_parcela' &&
    filtros.tipo_parcela &&
    normalizarTexto(item.tipo_parcela) !== normalizarTexto(filtros.tipo_parcela)
  ) {
    return false;
  }

  if (
    ignorar !== 'situacao' &&
    filtros.situacao &&
    normalizarTexto(item.situacao) !== normalizarTexto(filtros.situacao)
  ) {
    return false;
  }

  return true;
};

const calcularPercentualCobranca = item => {
  const valor = getNumero(item.valor);
  item.percentual_cobranca =
    valor === 0 ? 0 : getNumero(item.cobranca) / valor;
  item.fora_da_meta = item.percentual_cobranca < META_COBRANCA;
};

const criarLinhaEmpreendimento = item => ({
  id: `emp-${chaveEmpreendimento(item)}`,
  codigo_empreendimento: item.codigo_empreendimento,
  empreendimento: item.empreendimento,
  valor: 0,
  comissao: 0,
  cobranca: 0,
  acima_de_30: 0,
  acima_de_90: 0,
  acima_de_150: 0,
});

const criarLinhaTipoParcela = item => ({
  id: `tipo-${normalizarTexto(item.tipo_parcela)}`,
  tipo_parcela: item.tipo_parcela,
  valor_a_receber: 0,
  comissao: 0,
});

const ordenarPorTexto = chave => (a, b) =>
  normalizarTexto(a[chave]).localeCompare(normalizarTexto(b[chave]));

const montarLinhasComTotal = (linhas, total) => {
  if (linhas.length === 0) return linhas;
  return [...linhas, total];
};

const obterOuCriar = (mapa, chave, criarLinha) => {
  if (!mapa[chave]) {
    mapa[chave] = criarLinha();
  }

  return mapa[chave];
};

const agregarValoresEmpreendimento = (interacoes, filtros) => {
  const contratos = filtros.tipo_parcela
    ? normalizarLista(interacoes.contratos_por_tipo)
    : normalizarLista(interacoes.contratos);
  const mapa = {};

  contratos
    .filter(item => passaFiltros(item, filtros, 'empreendimento'))
    .forEach(item => {
      const chave = chaveEmpreendimento(item);
      const linha = obterOuCriar(mapa, chave, () =>
        criarLinhaEmpreendimento(item)
      );
      linha.valor += getNumero(item.valor);
      linha.cobranca += getNumero(item.cobranca);
      linha.acima_de_30 += getNumeroCampo(item, [
        'acima_de_30',
        'acima_de30',
      ]);
      linha.acima_de_90 += getNumeroCampo(item, [
        'acima_de_90',
        'acima_de90',
      ]);
      linha.acima_de_150 += getNumeroCampo(item, [
        'acima_de_150',
        'acima_de150',
      ]);
    });

  normalizarLista(interacoes.tipos_parcela)
    .filter(item => passaFiltros(item, filtros, 'empreendimento'))
    .forEach(item => {
      const chave = chaveEmpreendimento(item);
      const linha = obterOuCriar(mapa, chave, () =>
        criarLinhaEmpreendimento(item)
      );
      linha.comissao += getNumero(item.comissao);
    });

  const linhas = Object.keys(mapa)
    .map(chave => {
      const linha = mapa[chave];
      calcularPercentualCobranca(linha);
      return linha;
    })
    .sort(ordenarPorTexto('empreendimento'));

  const total = {
    id: 'total-valores-empreendimento',
    empreendimento: 'Total',
    valor: linhas.reduce((acc, item) => acc + getNumero(item.valor), 0),
    comissao: linhas.reduce((acc, item) => acc + getNumero(item.comissao), 0),
    cobranca: linhas.reduce((acc, item) => acc + getNumero(item.cobranca), 0),
    acima_de_30: linhas.reduce(
      (acc, item) => acc + getNumero(item.acima_de_30),
      0
    ),
    acima_de_90: linhas.reduce(
      (acc, item) => acc + getNumero(item.acima_de_90),
      0
    ),
    acima_de_150: linhas.reduce(
      (acc, item) => acc + getNumero(item.acima_de_150),
      0
    ),
    is_total: true,
  };
  calcularPercentualCobranca(total);

  return montarLinhasComTotal(linhas, total);
};

const agregarTiposParcela = (interacoes, filtros) => {
  const mapa = {};

  normalizarLista(interacoes.tipos_parcela)
    .filter(item => passaFiltros(item, filtros, 'tipo_parcela'))
    .forEach(item => {
      const chave = normalizarTexto(item.tipo_parcela);
      const linha = obterOuCriar(mapa, chave, () =>
        criarLinhaTipoParcela(item)
      );
      linha.valor_a_receber += getNumero(item.valor_a_receber);
      linha.comissao += getNumero(item.comissao);
    });

  const linhas = Object.keys(mapa)
    .map(chave => mapa[chave])
    .sort(ordenarPorTexto('tipo_parcela'));

  const total = {
    id: 'total-tipo-parcela',
    tipo_parcela: 'Total',
    valor_a_receber: linhas.reduce(
      (acc, item) => acc + getNumero(item.valor_a_receber),
      0
    ),
    comissao: linhas.reduce((acc, item) => acc + getNumero(item.comissao), 0),
    is_total: true,
  };

  return montarLinhasComTotal(linhas, total);
};

const agregarFluxoPorAno = (interacoes, filtros) => {
  const mapa = {};

  normalizarLista(interacoes.fluxo_por_ano)
    .filter(item => passaFiltros(item, filtros, null))
    .forEach(item => {
      const chave = normalizarTexto(item.ano);
      const linha = obterOuCriar(mapa, chave, () => ({
        id: `ano-${chave}`,
        ano: item.ano,
        valor: 0,
      }));
      linha.valor += getNumero(item.valor);
    });

  return Object.keys(mapa)
    .map(chave => mapa[chave])
    .sort((a, b) => getNumero(a.ano) - getNumero(b.ano));
};

const agregarSituacaoContrato = (interacoes, filtros) => {
  const contratos = filtros.tipo_parcela
    ? normalizarLista(interacoes.contratos_por_tipo)
    : normalizarLista(interacoes.contratos);
  const mapa = {};

  contratos
    .filter(item => passaFiltros(item, filtros, 'situacao'))
    .forEach(item => {
      const chave = normalizarTexto(item.situacao);
      const linha = obterOuCriar(mapa, chave, () => ({
        id: `situacao-${chave}`,
        situacao: item.situacao,
        valor: 0,
      }));
      linha.valor += getNumero(item.valor);
    });

  return Object.keys(mapa)
    .map(chave => mapa[chave])
    .sort(ordenarPorTexto('situacao'));
};

export const possuiInteracoes = interacoes =>
  normalizarLista(interacoes && interacoes.contratos).length > 0 ||
  normalizarLista(interacoes && interacoes.contratos_por_tipo).length > 0 ||
  normalizarLista(interacoes && interacoes.tipos_parcela).length > 0 ||
  normalizarLista(interacoes && interacoes.fluxo_por_ano).length > 0;

export const calcularDadosInterativos = (base, filtros) => {
  if (!possuiInteracoes(base.interacoes)) {
    return {
      valoresEmpreendimento: base.valoresEmpreendimento,
      tiposParcela: base.tiposParcela,
      fluxoPorAno: base.fluxoPorAno,
      situacaoContrato: base.situacaoContrato,
    };
  }

  return {
    valoresEmpreendimento: agregarValoresEmpreendimento(
      base.interacoes,
      filtros
    ),
    tiposParcela: agregarTiposParcela(base.interacoes, filtros),
    fluxoPorAno: agregarFluxoPorAno(base.interacoes, filtros),
    situacaoContrato: agregarSituacaoContrato(base.interacoes, filtros),
  };
};

export const criarFiltroEmpreendimento = item => ({
  codigo_empreendimento: item.codigo_empreendimento,
  empreendimento: item.empreendimento,
});

export const alternarFiltroVisual = (filtros, campo, valor) => {
  const atual = filtros[campo];
  const mesmoValor =
    campo === 'empreendimento'
      ? atual && mesmoEmpreendimento(atual, valor)
      : normalizarTexto(atual) === normalizarTexto(valor);

  return {
    ...filtros,
    [campo]: mesmoValor ? null : valor,
  };
};

export const empreendimentoEstaSelecionado = (filtros, item) =>
  filtros.empreendimento && mesmoEmpreendimento(filtros.empreendimento, item);

export const tipoParcelaEstaSelecionado = (filtros, item) =>
  filtros.tipo_parcela &&
  normalizarTexto(filtros.tipo_parcela) === normalizarTexto(item.tipo_parcela);

export const obterFiltrosVisuaisAtivos = filtros => {
  const ativos = [];

  if (filtros.empreendimento) {
    ativos.push({
      key: 'empreendimento',
      label: filtros.empreendimento.empreendimento,
    });
  }

  if (filtros.tipo_parcela) {
    ativos.push({ key: 'tipo_parcela', label: filtros.tipo_parcela });
  }

  if (filtros.situacao) {
    ativos.push({ key: 'situacao', label: filtros.situacao });
  }

  return ativos;
};
